import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject, of, throwError } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { IkigaiData, IkigaiAnswersDto } from "../models/ikigai.interface";
import { environment } from "../../../environments/environment";
import { BaseResponse } from "../models/base.response";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root",
})
export class IkigaiService {
  private localIkigaiData: IkigaiData = {
    passion: { answer: "", completed: false },
    mission: { answer: "", completed: false },
    vocation: { answer: "", completed: false },
    profession: { answer: "", completed: false },
    ikigai: { answer: "", completed: false },
  };

  constructor(private http: HttpClient, private authService: AuthService) {
    // Verificar si hay datos guardados en localStorage
    const savedData = localStorage.getItem("ikigaiData");
    if (savedData) {
      this.localIkigaiData = JSON.parse(savedData);
    }
  }

  getIkigaiData(): Observable<IkigaiData> {
    const userId = this.authService.getAuthUser()?.id;
    if (!userId) {
      console.warn(
        "User ID not found for fetching Ikigai data. Returning default."
      );
      return of(this.getDefaultIkigaiData());
    }

    return this.http
      .get<BaseResponse<IkigaiAnswersDto>>(
        `${environment.apiUrl}/users/${userId}/ikigai`
      )
      .pipe(
        map((response) => {
          const answers = response.data || {
            passion: "",
            mission: "",
            vocation: "",
            profession: "",
            ikigai: "",
          };
          // Mapear IkigaiAnswersDto a IkigaiData
          // La completitud se infiere de si hay respuesta. Esto podría necesitar ajuste si la lógica es diferente.
          this.localIkigaiData = {
            passion: {
              answer: answers.passion || "",
              completed: !!answers.passion,
            },
            mission: {
              answer: answers.mission || "",
              completed: !!answers.mission,
            },
            vocation: {
              answer: answers.vocation || "",
              completed: !!answers.vocation,
            },
            profession: {
              answer: answers.profession || "",
              completed: !!answers.profession,
            },
            ikigai: {
              answer: answers.ikigai || "",
              completed: !!answers.ikigai,
            },
          };
          console.log("Ikigai data fetched from backend:", this.localIkigaiData);
          this.saveToLocalStorage();
          return this.localIkigaiData;
        }),
        catchError((error) => {
          console.error("Error fetching Ikigai data:", error);
          return of(this.getDefaultIkigaiData());
        })
      );
  }

  private updateAnswerInBackend(
    field: "passion" | "mission" | "vocation" | "profession" | "ikigai",
    answer: string
  ): Observable<IkigaiAnswersDto> {
    const userId = this.authService.getAuthUser()?.id;
    if (!userId) {
      return throwError(
        () => new Error("User ID not found for updating Ikigai answer.")
      );
    }
    const payload = { field, answer };
    return this.http
      .patch<BaseResponse<IkigaiAnswersDto>>(
        `${environment.apiUrl}/users/${userId}/ikigai`,
        payload
      )
      .pipe(
        map((response) => response.data),
        catchError((error) => {
          console.error(`Error updating ${field} on backend:`, error);
          return throwError(() => error);
        })
      );
  }

  updatePassion(answer: string): Observable<IkigaiAnswersDto> {
    this.localIkigaiData.passion.answer = answer;
    this.localIkigaiData.passion.completed = !!answer.trim();
    this.saveToLocalStorage();
    return this.updateAnswerInBackend("passion", answer);
  }

  updateMission(answer: string): Observable<IkigaiAnswersDto> {
    this.localIkigaiData.mission.answer = answer;
    this.localIkigaiData.mission.completed = !!answer.trim();
    this.saveToLocalStorage();
    return this.updateAnswerInBackend("mission", answer);
  }

  updateVocation(answer: string): Observable<IkigaiAnswersDto> {
    this.localIkigaiData.vocation.answer = answer;
    this.localIkigaiData.vocation.completed = !!answer.trim();
    this.saveToLocalStorage();
    return this.updateAnswerInBackend("vocation", answer);
  }

  updateIkigai(answer: string): Observable<IkigaiAnswersDto> {
    this.localIkigaiData.ikigai.answer = answer;
    this.localIkigaiData.ikigai.completed = !!answer.trim();
    this.saveToLocalStorage();
    return this.updateAnswerInBackend("ikigai", answer);
  }

  updateProfession(answer: string): Observable<IkigaiAnswersDto> {
    this.localIkigaiData.profession.answer = answer;
    this.localIkigaiData.profession.completed = !!answer.trim();
    this.saveToLocalStorage();
    return this.updateAnswerInBackend("profession", answer);
  }

  getLocalIkigaiData(): IkigaiData {
    // Obtener datos locales sin hacer llamada HTTP - útil para PDF
    return { ...this.localIkigaiData };
  }

  generateSummary(): any {
    // Lógica para generar el resumen (puede ser local o del backend si es compleja)
    return this.localIkigaiData;
  }

  private saveToLocalStorage(): void {
    localStorage.setItem("ikigaiData", JSON.stringify(this.localIkigaiData));
  }

  private getDefaultIkigaiData(): IkigaiData {
    return {
      passion: { answer: "", completed: false },
      mission: { answer: "", completed: false },
      vocation: { answer: "", completed: false },
      profession: { answer: "", completed: false },
      ikigai: { answer: "", completed: false },
    };
  }

  resetIkigaiData(): void {
    this.localIkigaiData = {
      passion: { answer: "", completed: false },
      mission: { answer: "", completed: false },
      vocation: { answer: "", completed: false },
      profession: { answer: "", completed: false },
      ikigai: { answer: "", completed: false },
    };
    localStorage.removeItem("ikigaiData");
  }
}
